import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import type { FormatHookParams } from '/business/shared/components/pages/question/types';

import useAbsoluteEmissions from '/business/shared/components/pages/question/formats/staticSharedFields/useAbsoluteEmissions';
import useEmissionSource from '/business/shared/components/pages/question/formats/staticSharedFields/useEmissionSource';
import useHowWasCollected from '/business/shared/components/pages/question/formats/staticSharedFields/useHowWasCollected';
import useMakeIntensity from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeIntensity';
import useMakeQuantity from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeQuantity';
import useMakeUnit from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeUnit';
import {
  QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
  QUESTION_BRANCH_GROUP_KEY_INTENSITY,
} from '/business/shared/components/pages/question/questionEnums';
import WidgetIcon from '/business/shared/images/icons/expander-icon.svg';
import Button from '/shared/components/widgets/button/Button';
import WidgetAccordion from '/shared/components/widgets/widget-accordion/WidgetAccordion';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import {
  useGetQuantityDropdownValues,
  useGetUnitDropdownValues,
} from '/business/shared/components/pages/question/useFieldUnits';
import { useGetEmissionsFromQuestion } from '/business/shared/hooks/useGetEmissionsFromQuestion';

export default function useFormatSharedCustomScope1({
  question,
  questionFormatName,
  branchType,
}: Pick<FormatHookParams, 'question' | 'questionFormatName' | 'branchType'>) {
  const emissionSource = useEmissionSource({ subLabel: 'E.g. diesel' });
  const intensity = useMakeIntensity();
  const unitOfMeasurement = useMakeUnit();

  const quantityNoDropdown = useMakeQuantity({
    subLabel: 'E.g. for 100 items, enter 100',
  });

  const { mappedForDropdown: fuels } = useGetEmissionsFromQuestion({
    question: { apiKey: 'calculation-building-emissions-other' },
  });
  const quantity = useMakeQuantity({
    quantityDropdownValues: useGetQuantityDropdownValues({
      allRelevantUnits: true,
      questionFormatName,
      emissionSourcesArray: fuels,
    }),
    subLabel: 'E.g. for 100 items, enter 100',
  });
  const howWasCollected = useHowWasCollected();
  const absoluteEmissions = useAbsoluteEmissions({
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(true),
  });

  const pageSchema = useMemo(() => {
    return [
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Intensity value/unit
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data based on carbon intensity/unit (e.g., kg CO₂e per unit of product).
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Absolute emissions
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data as a total sum (total kg or tonnes CO₂e) in relation to products or services your
                  business used.
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...emissionSource,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...emissionSource,
      },

      // add the shared 'quantity + howWasCollected'
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...intensity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...unitOfMeasurement,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...absoluteEmissions,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...quantityNoDropdown,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...quantity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...howWasCollected,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...howWasCollected,
      },
      // inject the buttons
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_INTENSITY}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_INTENSITY);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_ABSOLUTE}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_ABSOLUTE);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
    ];
  }, []);

  const [saveCallback, setSaveCallback] = useState<null | 'saveNew'>(null);
  const [questionGroupForValidation, setQuestionGroupForValidation] = useState<null | string>(null);

  return {
    pageSchema,
    saveCallback,
    questionGroupForValidation,
  };
}
